import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import {
  ArrowLeft, Camera, MapPin, Calendar, Plus, Send,
  Cloud, AlertTriangle, CheckCircle, Image, X, Loader2
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DIFFICULTY_OPTIONS = [
  { value: "chuva", label: "Chuva", icon: Cloud },
  { value: "material", label: "Atraso de Material", icon: AlertTriangle },
  { value: "mao_de_obra", label: "Falta de Mão de Obra", icon: AlertTriangle },
  { value: "outros", label: "Outros", icon: AlertTriangle }
];

export default function ProjectDiaryPage() {
  const { projectId } = useParams();
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [location, setLocation] = useState(null);
  
  const [formData, setFormData] = useState({
    progress_text: "",
    difficulties: "",
    photos: []
  });

  useEffect(() => {
    fetchEntries();
  }, [projectId, token]);

  const fetchEntries = async () => {
    try {
      const res = await fetch(`${API_URL}/api/projects/${projectId}/diary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setEntries(await res.json());
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleGetLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          });
          toast.success("Localização registrada!");
        },
        (error) => {
          toast.error("Não foi possível obter a localização");
        }
      );
    }
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const newPhotos = [];
    for (const file of files) {
      const reader = new FileReader();
      reader.onload = () => {
        newPhotos.push(reader.result);
        if (newPhotos.length === files.length) {
          setFormData(prev => ({
            ...prev,
            photos: [...prev.photos, ...newPhotos]
          }));
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemovePhoto = (index) => {
    setFormData(prev => ({
      ...prev,
      photos: prev.photos.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async () => {
    if (!formData.progress_text.trim()) {
      toast.error("Descreva o progresso do dia");
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/projects/${projectId}/diary`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          progress_text: formData.progress_text,
          difficulties: formData.difficulties,
          photos: formData.photos,
          location_checkin: location
        })
      });

      if (res.ok) {
        toast.success("Entrada registrada!");
        setShowForm(false);
        setFormData({ progress_text: "", difficulties: "", photos: [] });
        setLocation(null);
        fetchEntries();
      } else {
        toast.error("Erro ao registrar entrada");
      }
    } catch (error) {
      toast.error("Erro ao registrar entrada");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen-mobile flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-neutral-200 border-t-neutral-900 rounded-full animate-spin"></div>
      </div>
    );
  }

  const isProvider = user?.role === "provider";

  return (
    <div className="min-h-screen-mobile bg-white pb-nav" data-testid="project-diary-page">
      {/* Header */}
      <header className="sticky-header border-b border-neutral-100">
        <div className="px-5 h-14 flex items-center gap-3">
          <button 
            onClick={() => navigate(`/project/${projectId}`)}
            className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-neutral-50 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1">
            <h1 className="font-semibold text-neutral-900">Diário de Obra</h1>
            <p className="text-xs text-neutral-500">{entries.length} registros</p>
          </div>
          {isProvider && !showForm && (
            <Button
              onClick={() => setShowForm(true)}
              className="btn-minimal btn-primary h-9 px-4 text-sm"
            >
              <Plus className="w-4 h-4 mr-1" />
              Novo
            </Button>
          )}
        </div>
      </header>

      <main className="px-5 py-6">
        {/* New Entry Form */}
        {showForm && (
          <div className="card-clean p-5 mb-6 animate-up" data-testid="diary-form">
            <h3 className="font-semibold text-neutral-900 mb-4">Nova Entrada</h3>
            
            {/* Progress Text */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Progresso do dia *
              </label>
              <textarea
                value={formData.progress_text}
                onChange={(e) => setFormData(prev => ({ ...prev, progress_text: e.target.value }))}
                placeholder="Descreva o que foi realizado hoje..."
                className="input-minimal h-24 resize-none py-3"
                data-testid="progress-input"
              />
            </div>

            {/* Difficulties */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Dificuldades (opcional)
              </label>
              <div className="flex flex-wrap gap-2">
                {DIFFICULTY_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => setFormData(prev => ({ 
                      ...prev, 
                      difficulties: prev.difficulties === opt.value ? "" : opt.value 
                    }))}
                    className={`px-3 py-2 rounded-lg text-sm transition-all ${
                      formData.difficulties === opt.value
                        ? "bg-amber-100 text-amber-700 border border-amber-300"
                        : "bg-neutral-100 text-neutral-600 border border-transparent"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Photos */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Fotos
              </label>
              <div className="flex gap-2 flex-wrap">
                {formData.photos.map((photo, index) => (
                  <div key={index} className="relative w-20 h-20 rounded-lg overflow-hidden">
                    <img src={photo} alt="" className="w-full h-full object-cover" />
                    <button
                      onClick={() => handleRemovePhoto(index)}
                      className="absolute top-1 right-1 w-5 h-5 bg-black/50 rounded-full flex items-center justify-center"
                    >
                      <X className="w-3 h-3 text-white" />
                    </button>
                  </div>
                ))}
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="w-20 h-20 rounded-lg border-2 border-dashed border-neutral-300 flex items-center justify-center hover:border-neutral-400 transition-colors"
                >
                  <Camera className="w-6 h-6 text-neutral-400" />
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handlePhotoUpload}
                  className="hidden"
                />
              </div>
            </div>

            {/* Location */}
            <div className="mb-6">
              <button
                onClick={handleGetLocation}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                  location
                    ? "bg-emerald-100 text-emerald-700"
                    : "bg-neutral-100 text-neutral-600 hover:bg-neutral-200"
                }`}
              >
                <MapPin className="w-4 h-4" />
                {location ? "Localização registrada ✓" : "Registrar localização"}
              </button>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <Button
                onClick={() => {
                  setShowForm(false);
                  setFormData({ progress_text: "", difficulties: "", photos: [] });
                  setLocation(null);
                }}
                className="btn-minimal btn-secondary flex-1"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={submitting}
                className="btn-minimal btn-primary flex-1"
                data-testid="submit-diary-btn"
              >
                {submitting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Registrar
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Entries List */}
        <div className="space-y-4">
          {entries.map((entry, index) => (
            <div 
              key={entry.entry_id} 
              className="card-clean p-4 animate-up"
              style={{ animationDelay: `${index * 50}ms` }}
            >
              {/* Header */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-neutral-400" />
                  <span className="text-sm font-medium text-neutral-900">
                    {new Date(entry.date).toLocaleDateString("pt-BR", {
                      weekday: "short",
                      day: "numeric",
                      month: "short"
                    })}
                  </span>
                </div>
                {entry.client_approved && (
                  <span className="badge-success text-xs">
                    <CheckCircle className="w-3 h-3" />
                    Aprovado
                  </span>
                )}
              </div>

              {/* Content */}
              <p className="text-neutral-700">{entry.progress_text}</p>

              {/* Difficulties */}
              {entry.difficulties && (
                <div className="mt-3 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-500" />
                  <span className="text-sm text-amber-700">{entry.difficulties}</span>
                </div>
              )}

              {/* Photos */}
              {entry.photos?.length > 0 && (
                <div className="mt-3 flex gap-2 overflow-x-auto pb-2">
                  {entry.photos.map((photo, i) => (
                    <img
                      key={i}
                      src={photo}
                      alt=""
                      className="w-24 h-24 rounded-lg object-cover flex-shrink-0"
                    />
                  ))}
                </div>
              )}

              {/* Location */}
              {entry.location_checkin && (
                <div className="mt-3 flex items-center gap-2 text-xs text-neutral-500">
                  <MapPin className="w-3 h-3" />
                  <span>Check-in registrado</span>
                </div>
              )}

              {/* AI Summary */}
              {entry.ai_summary && (
                <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-700">{entry.ai_summary}</p>
                </div>
              )}

              {/* Client Comment */}
              {entry.client_comment && (
                <div className="mt-3 p-3 bg-neutral-50 rounded-lg">
                  <p className="text-xs text-neutral-500 mb-1">Comentário do cliente:</p>
                  <p className="text-sm text-neutral-700">{entry.client_comment}</p>
                </div>
              )}
            </div>
          ))}
        </div>

        {entries.length === 0 && !showForm && (
          <div className="card-subtle p-8 text-center">
            <Camera className="w-12 h-12 text-neutral-300 mx-auto mb-4" />
            <p className="font-medium text-neutral-900">Nenhum registro</p>
            <p className="text-sm text-neutral-500 mt-1">
              {isProvider 
                ? "Registre o progresso diário da obra"
                : "O prestador ainda não registrou entradas"
              }
            </p>
          </div>
        )}
      </main>
    </div>
  );
}
